import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Phone, Lock, Eye, EyeOff, CheckCircle2, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import PersonalizePicker from "../components/PersonalizePicker";
import { api } from "../lib/api";

export default function Register() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { registerUser, personalize } = useAuth();
  const [step, setStep] = useState(1);   // 1 = form, 2 = personalize
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ full_name: "", mobile: "", password: "", confirm: "" });
  const [refCode, setRefCode] = useState("");
  const [refInfo, setRefInfo] = useState(null); // { valid, referrer }

  // Capture ?ref=... exactly once and validate against the server
  useEffect(() => {
    const raw = (params.get("ref") || "").trim().toUpperCase();
    if (!raw) return;
    setRefCode(raw);
    api.referralsValidate(raw).then((r) => {
      if (r?.valid) setRefInfo(r);
      else setRefInfo({ valid: false });
    }).catch(() => setRefInfo({ valid: false }));
  }, [params]);

  const submitRegister = async (e) => {
    e.preventDefault();
    if (form.full_name.trim().length < 2) return toast.error("Enter your full name");
    if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) return toast.error("Enter a valid 10-digit Indian mobile number");
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await registerUser({
        full_name: form.full_name.trim(),
        mobile: form.mobile.trim(),
        password: form.password,
        referral_code: refInfo?.valid ? refCode : undefined,
      });
      toast.success("Account created — one last step");
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const submitPersonalize = async ({ lucky_number, avatar_id }) => {
    setBusy(true);
    try {
      await personalize(lucky_number, avatar_id);
      toast.success("All set — welcome aboard 🎉");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save selection");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="register-page">
      {step === 1 && (
        <>
          {refInfo?.valid && (
            <div className="mb-4 rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-emerald-50 p-4 flex items-start gap-3" data-testid="register-invite-banner">
              <span className="h-10 w-10 rounded-lg bg-amber-500 text-white grid place-items-center shrink-0"><Gift size={18}/></span>
              <div className="flex-1">
                <div className="font-display font-extrabold text-amber-900">🎁 You&rsquo;ve Been Invited!</div>
                <p className="text-sm text-amber-900/80">
                  Join PM POSHAN Calculator using <b>{refInfo.referrer?.name || "a teacher"}</b>&rsquo;s referral (<code className="font-mono">{refCode}</code>). Complete registration and Premium purchase to support your referrer.
                </p>
              </div>
            </div>
          )}
          <div>
            <div className="chip chip-green"><UserPlus size={14}/> Create your account</div>
            <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Get started free</h1>
            <p className="mt-1 text-slate-600">You&rsquo;ll be able to save daily entries, view your history and unlock reports (Premium ₹299/year — Launch Offer).</p>
          </div>

          <ol className="mt-6 flex items-center gap-2 text-xs font-semibold" data-testid="register-steps">
            {[{ n: 1, label: "Details" }, { n: 2, label: "Personalize" }].map((s, i) => (
              <li key={s.n} className="flex items-center gap-2">
                <span className={`h-7 w-7 grid place-items-center rounded-full border ${step >= s.n ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-500 border-slate-300"}`}>
                  {step > s.n ? <CheckCircle2 size={14}/> : s.n}
                </span>
                <span className={step >= s.n ? "text-slate-900" : "text-slate-500"}>{s.label}</span>
                {i < 1 && <span className={`h-0.5 w-10 ${step > s.n ? "bg-emerald-700" : "bg-slate-200"}`}/>}
              </li>
            ))}
          </ol>

          <form onSubmit={submitRegister} className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="r-name" className="text-sm font-semibold">Full name *</Label>
              <Input id="r-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                     className="h-11 mt-1.5" placeholder="e.g. Ramesh Kumar" data-testid="register-name"/>
            </div>
            <div>
              <Label htmlFor="r-mobile" className="text-sm font-semibold flex items-center gap-1"><Phone size={12}/> Mobile number *</Label>
              <Input id="r-mobile" value={form.mobile}
                     onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                     className="h-11 mt-1.5" placeholder="10-digit Indian mobile" inputMode="numeric" maxLength={10}
                     data-testid="register-mobile"/>
              <p className="text-xs text-slate-500 mt-1">Used for login. Kept private.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="r-pw" className="text-sm font-semibold flex items-center gap-1"><Lock size={12}/> Password *</Label>
                <div className="mt-1.5 relative">
                  <Input id="r-pw" type={show ? "text" : "password"} value={form.password}
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         className="h-11 pr-10" placeholder="Min 6 characters" data-testid="register-password"/>
                  <button type="button" onClick={() => setShow((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800" data-testid="register-toggle-pw">
                    {show ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="r-confirm" className="text-sm font-semibold">Confirm password *</Label>
                <Input id="r-confirm" type={show ? "text" : "password"} value={form.confirm}
                       onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                       className="h-11 mt-1.5" placeholder="Retype your password" data-testid="register-confirm"/>
              </div>
            </div>
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3">
              <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white h-11 px-6" data-testid="register-submit">
                {busy ? "Creating account…" : "Create free account"} <UserPlus size={16}/>
              </Button>
              <span className="text-sm text-slate-600">Already have an account? <Link to="/login" className="text-emerald-700 font-semibold hover:underline" data-testid="register-to-login">Sign in</Link></span>
            </div>
            <p className="text-xs text-slate-500 pt-2">
              Forgot password recovery is admin-assisted — contact your PM Poshan admin if you ever lose access.
            </p>
          </form>
        </>
      )}

      {step === 2 && (
        <>
          <div className="mb-4 flex items-center justify-between gap-3 text-xs font-semibold text-slate-500">
            <span>Step 2 of 2</span>
            <div className="flex-1 grid grid-cols-2 gap-2 max-w-[220px]">
              <span className="h-1.5 rounded-full bg-emerald-600"/>
              <span className="h-1.5 rounded-full bg-emerald-400"/>
            </div>
          </div>
          <PersonalizePicker busy={busy} onSubmit={submitPersonalize}/>
        </>
      )}
    </div>
  );
}
